import { createElement, useMemo, type MouseEvent as ReactMouseEvent } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import type { BarCustomLayerProps } from '@nivo/bar';
import { useTooltip } from '@nivo/tooltip';
import { tcesToHeatmapColor } from '@/utils/buildGtpHeatmapData';
import { buildGtpChartRows, type GeneGtpItem, type GtpChartRow } from '@/utils/formatGtpList';
import { GTP_BAR_RADIUS, roundedOuterBarPath } from './roundedOuterBarPath';

const CHART_THEME = {
  axis: {
    ticks: { text: { fontSize: 10, fill: '#64748b' } },
    legend: { text: { fontSize: 11, fill: '#475569', fontWeight: 500 } },
  },
  grid: { line: { stroke: '#eef2f7', strokeWidth: 1 } },
};

type GtpChartProps = {
  items: GeneGtpItem[];
};

function OuterRoundedBars({
  bars,
  isInteractive,
  tooltip,
}: Pick<BarCustomLayerProps<GtpChartRow>, 'bars' | 'isInteractive' | 'tooltip'>) {
  const { showTooltipFromEvent, hideTooltip } = useTooltip();

  return (
    <g>
      {bars.map(bar => {
        const value = Number(bar.data.value);
        const d = roundedOuterBarPath(bar.width, bar.height, value, GTP_BAR_RADIUS);
        if (!d) return null;

        const showTip = (event: ReactMouseEvent) => {
          showTooltipFromEvent(
            createElement(tooltip, {
              ...bar.data,
              color: bar.color,
              label: bar.label,
              value,
            }),
            event,
          );
        };

        return (
          <path
            key={bar.key}
            transform={`translate(${bar.x},${bar.y})`}
            d={d}
            fill={bar.color}
            onMouseEnter={isInteractive ? showTip : undefined}
            onMouseMove={isInteractive ? showTip : undefined}
            onMouseLeave={isInteractive ? hideTooltip : undefined}
          />
        );
      })}
    </g>
  );
}

function RowLabels({
  bars,
  labelById,
  fullNameById,
}: Pick<BarCustomLayerProps<GtpChartRow>, 'bars'> & {
  labelById: Record<string, string>;
  fullNameById: Record<string, string>;
}) {
  return (
    <g pointerEvents="none">
      {bars.map(bar => {
        const id = String(bar.data.indexValue);
        const label = labelById[id] ?? id;
        const y = bar.y + bar.height / 2;

        return (
          <text
            key={bar.key}
            x={-8}
            y={y}
            textAnchor="end"
            dominantBaseline="central"
            style={{ fontSize: 10, fill: '#374151', fontWeight: 500 }}
          >
            <title>{fullNameById[id] ?? label}</title>
            {label}
          </text>
        );
      })}
    </g>
  );
}

export function GtpChart({ items }: GtpChartProps) {
  const chartData = useMemo(() => buildGtpChartRows(items), [items]);

  const labelById = useMemo(() => Object.fromEntries(chartData.map(row => [row.id, row.label])), [chartData]);

  const fullNameById = useMemo(
    () => Object.fromEntries(chartData.map(row => [row.id, row.fullName])),
    [chartData],
  );

  const tcesExtent = useMemo(() => {
    const absMax = Math.max(...chartData.map(row => Math.abs(row.tces)), 1);
    const pad = absMax * 0.12;
    return { min: -(absMax + pad), max: absMax + pad };
  }, [chartData]);

  const colorDomain = useMemo(() => {
    let maxPositive = 0;
    let minNegative = 0;
    chartData.forEach(row => {
      if (row.tces > maxPositive) maxPositive = row.tces;
      if (row.tces < minNegative) minNegative = row.tces;
    });
    return { maxPositive, minNegative };
  }, [chartData]);

  const leftMargin = useMemo(() => {
    const longest = Math.max(...chartData.map(row => row.label.length), 12);
    return Math.min(220, Math.max(148, longest * 6.1 + 16));
  }, [chartData]);

  if (!chartData.length) {
    return <p className="px-3 py-6 text-center text-sm text-gray-400">No GTP data available.</p>;
  }

  return (
    <ResponsiveBar
      data={chartData}
      keys={['tces']}
      indexBy="id"
      layout="horizontal"
      margin={{ top: 8, right: 12, bottom: 40, left: leftMargin }}
      padding={0.28}
      valueScale={{ type: 'linear', min: tcesExtent.min, max: tcesExtent.max }}
      colors={bar =>
        tcesToHeatmapColor(Number(bar.data.tces), colorDomain.maxPositive, colorDomain.minNegative)
      }
      theme={CHART_THEME}
      enableGridX
      enableGridY={false}
      markers={[
        {
          axis: 'x',
          value: 0,
          lineStyle: { stroke: '#94a3b8', strokeWidth: 1 },
        },
      ]}
      axisBottom={{
        legend: 'TCES',
        legendOffset: 32,
        legendPosition: 'middle',
        tickSize: 0,
        tickPadding: 6,
      }}
      axisLeft={{
        tickSize: 0,
        renderTick: () => <g />,
      }}
      enableLabel={false}
      layers={[
        'grid',
        'axes',
        OuterRoundedBars,
        'markers',
        props => <RowLabels {...props} labelById={labelById} fullNameById={fullNameById} />,
      ]}
      tooltip={({ indexValue, value, data: barData }) => (
        <div className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs shadow-md">
          <strong className="block text-slate-800">{String(barData.fullName ?? indexValue)}</strong>
          <span className="text-slate-500">TCES: {Number(value).toFixed(2)}</span>
        </div>
      )}
    />
  );
}
